import { Download, Save } from '@carbon/icons-react';
import { Button, ComboBox, FileUploaderButton, InlineNotification, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import { asProblem, hasPermission, languageName, ListTable, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';
import { ErrorNotice, TransientNotice } from '../../common/Notices';
import { brandsKey, listAllPages, scopeApis } from '../../delivery/deliveryApi';
import {
  downloadTexts,
  getSummary,
  listTexts,
  MAX_CHANGES,
  saveTexts,
  TEXT_KINDS,
  translationKeys,
  TRANSLATIONS_MANAGE,
  type SaveTranslationsResult,
  type TranslationChange,
  type TranslationText,
} from './translationsApi';
import { chunk, CsvHeaderError, toChanges } from './translationsCsv';
import './translations.scss';

const editKey = (text: TranslationText, language: string) => `${text.kind}|${text.id}|${text.field}|${language}`;
const rowId = (text: TranslationText) => `${text.kind}|${text.id}|${text.field}`;

/** Totals of several saves, with the refused changes' positions in the whole list. */
async function saveInParts(brandId: string, changes: TranslationChange[]) {
  let updated = 0;
  let unchanged = 0;
  const refused: number[] = [];
  let offset = 0;
  for (const part of chunk(changes, MAX_CHANGES)) {
    const result: SaveTranslationsResult = await saveTexts(brandId, part);
    updated += Number(result.updated);
    unchanged += Number(result.unchanged);
    refused.push(...result.rejected.map((r) => offset + Number(r.index)));
    offset += part.length;
  }
  return { updated, unchanged, refused };
}

/**
 * Translating a brand's customer-facing catalog texts (FR-ONL-19, ADR 0071): what each language still needs, the texts with
 * a box per language (only the changed ones are saved), and a CSV to send to a translator and import back.
 */
export function TranslationsPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, TRANSLATIONS_MANAGE);
  const queryClient = useQueryClient();
  const brands = useQuery({ queryKey: brandsKey, queryFn: () => listAllPages(scopeApis.brands) });
  const summary = useQuery({ queryKey: translationKeys.summary, queryFn: getSummary });
  const [brandId, setBrandId] = useState<string>();
  const brand = brandId ?? brands.data?.[0]?.id;
  const [kind, setKind] = useState('');
  const [missingIn, setMissingIn] = useState('');
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search.trim());
  const [paging, setPaging] = useState({ page: 1, pageSize: 50 });
  const texts = useQuery({
    queryKey: translationKeys.page(brand ?? '', kind, missingIn, query, paging.page, paging.pageSize),
    queryFn: () => listTexts({ brandId: brand ?? '', kind, missingIn, search: query, ...paging }),
    enabled: !!brand,
    placeholderData: keepPreviousData,
  });
  const brandSummary = summary.data?.find((b) => b.brandId === brand);
  const languages = texts.data?.languages ?? brandSummary?.languages ?? [];
  const defaultLanguage = texts.data?.defaultLanguage ?? brandSummary?.defaultLanguage;
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const pending = Object.keys(edits).length;

  const refresh = () => queryClient.invalidateQueries({ queryKey: translationKeys.all });
  const save = useMutation({
    mutationFn: () => {
      const changes = Object.entries(edits).map(([key, text]): TranslationChange => {
        const [k = '', id = '', field = '', language = ''] = key.split('|');
        return { kind: k, id, field, language, text };
      });
      return saveInParts(brand ?? '', changes);
    },
    onSuccess: async ({ updated, refused }) => {
      setEdits({});
      setProblem(refused.length ? t('translations.refused', { count: refused.length }) : undefined);
      setNotice(t('translations.saved', { count: updated }));
      await refresh();
    },
    onError: (error) => setProblem(problemMessage(t, asProblem(error))),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { changes, lines } = toChanges(await file.text());
      const result = await saveInParts(brand ?? '', changes);
      return { ...result, lines: [...new Set(result.refused.map((i) => lines[i]))] };
    },
    onSuccess: async ({ updated, unchanged, lines }) => {
      setProblem(lines.length ? t('translations.importRefused', { count: lines.length, lines: lines.slice(0, 20).join(', ') }) : undefined);
      setNotice(t('translations.imported', { updated, unchanged }));
      await refresh();
    },
    onError: (error) =>
      setProblem(error instanceof CsvHeaderError ? t('translations.importHeader') : problemMessage(t, asProblem(error))),
  });
  const download = useMutation({
    mutationFn: () => downloadTexts(brand ?? ''),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translations-${brands.data?.find((b) => b.id === brand)?.name ?? brand}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: () => setProblem(t('translations.downloadFailed')),
  });

  const kindLabel = (k: string) => t(`translations.kinds.${k}` as I18nKey);
  const rows = (texts.data?.items ?? []).map((text) => ({ ...text, id: rowId(text), textId: text.id }));

  return (
    <section className="pos-admin-page pos-translations">
      <PageHeader
        title={t('translations.title')}
        description={t('translations.intro')}
        actions={
          brand ? (
            <>
              <Button kind="tertiary" renderIcon={Download} disabled={download.isPending} onClick={() => download.mutate()}>
                {t('translations.download')}
              </Button>
              {canEdit && (
                <FileUploaderButton
                  labelText={t('translations.import')}
                  buttonKind="tertiary"
                  accept={['.csv']}
                  disabled={upload.isPending}
                  disableLabelChanges
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (file) upload.mutate(file);
                    e.target.value = '';
                  }}
                />
              )}
              {canEdit && (
                <Button renderIcon={Save} disabled={pending === 0 || save.isPending} onClick={() => save.mutate()}>
                  {pending ? t('translations.saveCount', { count: pending }) : t('translations.save')}
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      {brands.error && <ErrorNotice title={problemMessage(t, asProblem(brands.error))} onRetry={() => void brands.refetch()} />}
      {problem && <InlineNotification kind="error" role="alert" lowContrast title={problem} onClose={() => setProblem(undefined)} />}
      {notice && <TransientNotice kind="success" title={notice} onDone={() => setNotice(undefined)} />}
      {upload.isPending && <InlineNotification kind="info" role="status" lowContrast hideCloseButton title={t('translations.importing')} />}
      <div className="pos-translations__filters">
        <ComboBox
          id="translations-brand"
          titleText={t('translations.brand')}
          items={brands.data ?? []}
          itemToString={(b) => b?.name ?? ''}
          selectedItem={brands.data?.find((b) => b.id === brand) ?? null}
          onChange={({ selectedItem }) => {
            if (!selectedItem) return;
            setBrandId(selectedItem.id);
            setEdits({});
            setMissingIn('');
            setPaging((p) => ({ ...p, page: 1 }));
          }}
        />
        <Select
          id="translations-kind"
          labelText={t('translations.kind')}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            setPaging((p) => ({ ...p, page: 1 }));
          }}
        >
          <SelectItem value="" text={t('translations.allKinds')} />
          {TEXT_KINDS.map((k) => (
            <SelectItem key={k} value={k} text={kindLabel(k)} />
          ))}
        </Select>
        <Select
          id="translations-show"
          labelText={t('translations.show')}
          value={missingIn}
          onChange={(e) => {
            setMissingIn(e.target.value);
            setPaging((p) => ({ ...p, page: 1 }));
          }}
        >
          <SelectItem value="" text={t('translations.allTexts')} />
          {languages.map((l) => (
            <SelectItem key={l} value={l} text={t('translations.missingIn', { language: languageName(l) })} />
          ))}
        </Select>
      </div>
      {brandSummary && (
        <div className="pos-translations__summary" aria-label={t('translations.summary')}>
          {!brandSummary.hasSite && <InlineNotification kind="info" lowContrast hideCloseButton title={t('translations.noSite')} />}
          <p>
            {brandSummary.gaps.map((gap) => (
              <Tag key={gap.language} type={Number(gap.missing) > 0 ? 'red' : 'green'}>
                {Number(gap.missing) > 0
                  ? t('translations.missingCount', { language: languageName(gap.language), count: Number(gap.missing) })
                  : t('translations.complete', { language: languageName(gap.language) })}
              </Tag>
            ))}
          </p>
        </div>
      )}
      {brand && (
        <ListTable
          ariaLabel={t('translations.title')}
          loading={texts.isLoading}
          error={texts.error ? problemMessage(t, asProblem(texts.error)) : undefined}
          onRetry={() => void texts.refetch()}
          search={{ label: t('translations.search'), onChange: (value) => {
            setSearch(value);
            setPaging((p) => ({ ...p, page: 1 }));
          } }}
          rows={rows}
          empty={{ heading: missingIn ? t('translations.emptyMissing') : t('translations.empty') }}
          pagination={{ page: paging.page, pageSize: paging.pageSize, total: Number(texts.data?.total ?? 0), onChange: setPaging }}
          columns={[
            {
              key: 'source',
              header: t('translations.columns.text'),
              render: (text) => (
                <div className="pos-translations__text">
                  <span>{text.source || text.context}</span>
                  <span className="pos-translations__meta">
                    {[kindLabel(text.kind), t(`translations.fields.${text.field}` as I18nKey), text.source ? text.context : null].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ),
            },
            ...languages.map((language) => ({
              key: `lang-${language}`,
              header: language === defaultLanguage ? t('translations.defaultLanguage', { language: languageName(language) }) : languageName(language),
              render: (text: (typeof rows)[number]) => {
                const key = editKey({ ...text, id: text.textId }, language);
                const saved = text.texts[language] ?? '';
                return (
                  <TextInput
                    id={`tr-${key}`}
                    size="sm"
                    hideLabel
                    labelText={`${languageName(language)}: ${text.source || text.context || ''}`}
                    value={edits[key] ?? saved}
                    placeholder={language === defaultLanguage ? text.source : undefined}
                    readOnly={!canEdit}
                    warn={text.missing.includes(language) && edits[key] === undefined}
                    warnText={t('translations.missing')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Typed back to what is saved: no longer a change.
                      setEdits((current) =>
                        value === saved ? Object.fromEntries(Object.entries(current).filter(([k]) => k !== key)) : { ...current, [key]: value },
                      );
                    }}
                  />
                );
              },
            })),
          ]}
        />
      )}
    </section>
  );
}
